import { Label } from '@/components/ui/label'
import { useCallback, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAudit } from '../context'

type Props = {
  label: string
  children: React.ReactNode
}

const parseOverrideValue = <T extends string | string[] | undefined>(
  raw: string | undefined,
  initialValue: T,
  validOptions?: readonly string[],
): T => {
  if (raw === undefined || raw === null) return initialValue

  if (Array.isArray(initialValue)) {
    const values = raw === '' ? [] : raw.split(',')
    const filtered = validOptions
      ? values.filter((v) => validOptions.includes(v))
      : values
    return filtered as T
  }

  if (validOptions && !validOptions.includes(raw)) return initialValue

  return raw as T
}

export const useAuditFilterState = <T extends string | string[] | undefined>(
  key: string,
  initialValue: T,
  validOptions?: readonly string[],
) => {
  const { filterOverrides } = useAudit()
  const { search } = useLocation()
  const navigate = useNavigate()

  const [value, setValue] = useState<T>(() => {
    const params = new URLSearchParams(search)
    const raw = params.get(key) ?? localStorage.getItem(`audit-filter-${key}`)
    if (raw === null) return initialValue

    if (Array.isArray(initialValue)) {
      const values = raw === '' ? [] : raw.split(',')
      const filtered = validOptions
        ? values.filter((v) => validOptions.includes(v))
        : values
      return filtered as T
    }

    if (validOptions && !validOptions.includes(raw)) return initialValue

    return raw as T
  })

  const updateValue = useCallback(
    (newValue: T) => {
      setValue(newValue)

      const params = new URLSearchParams(window.location.search)

      if (newValue == null) {
        localStorage.removeItem(`audit-filter-${key}`)
        params.delete(key)
      } else {
        const serialized = Array.isArray(newValue)
          ? newValue.join(',')
          : (newValue as string)
        localStorage.setItem(`audit-filter-${key}`, serialized)
        params.set(key, serialized)
      }

      const searchString = params.toString()
      navigate(
        {
          search: searchString ? `?${searchString}` : '',
          hash: window.location.hash,
        },
        { replace: true },
      )
    },
    [key, navigate],
  )

  if (filterOverrides && key in filterOverrides) {
    return {
      value: parseOverrideValue(
        filterOverrides[key],
        initialValue,
        validOptions,
      ),
      setValue: (_newValue: T) => null,
    }
  }

  return {
    value,
    setValue: updateValue,
  }
}

const AuditFilter = ({ label, children }: Props) => {
  return (
    <div className='flex flex-col gap-1'>
      <Label>{label}</Label>
      {children}
    </div>
  )
}

export default AuditFilter
